import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { BarcodeScannerInstanceToken, BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import {
    AbstractControl,
    COMPOSITION_BUFFER_MODE,
    FormArray,
    FormBuilder,
    FormControl,
    ValidationErrors,
} from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { iif, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { TextModelOptions } from '../../models/options/text-model-options';
import { InternalInfoMultilineTextModel } from '../../models/primitive/internal-info-multiline-text.model';
import { MultilineTextModel } from '../../models/primitive/multiline-text.model';
import { TextModel } from '../../models/primitive/text.model';
import { AppNotificationType } from '../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../services/app-notification/app-notification.service';
import { isDefined } from '../../utilities/is-defined';
import { matchAndProcessBarcode } from '@evorait/pace-components';

export enum TEXT_INPUT_MODEL {
    MULTILINE_TEXT_MODEL = 'MultilineTextModel',
    INTERNAL_INFO_MULTILINE_TEXT_MODEL = 'InternalInfoMultilineTextModel',
    TEXT_MODEL = 'TextModel',
}

// TODO: fix me

@Component({
    selector: 'pace-core-text-input',
    templateUrl: './text-input.component.html',
    styleUrls: ['./text-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    /*
    If you use a keyboard where the generated output is composited out of multiple inputs from the user (like
    Japanese or Korean), then angular will wait for the final composed result from the keyboard and not process the individual inputs.
    If you need access to the individual input actions, then you can disable this waiting for the composition end event by setting COMPOSITION_BUFFER_MODE to false.
    https://angular.io/api/forms/COMPOSITION_BUFFER_MODE
    https://github.com/angular/angular/issues/15947#issuecomment-299472733
     */
    providers: [{ provide: COMPOSITION_BUFFER_MODE, useValue: false }],
    standalone: false,
})
export class TextInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: TextModel | MultilineTextModel | InternalInfoMultilineTextModel;

    @Input() public inputMode?: string;

    @Input()
    public ariaLabel = 'Text Input';

    @Output()
    public modelChange = new EventEmitter<TextModel | MultilineTextModel | InternalInfoMultilineTextModel>();

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public barcodeClipboardAccessible = true;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<TextModel>>();

    @Output()
    public scanResults = new EventEmitter<string>();

    @Output()
    public blurEvent = new EventEmitter<string>();

    @ViewChild('textInput')
    private textInput: ElementRef;

    @ViewChild('textAreaInput')
    private textAreaInput: ElementRef;

    @ViewChild('internalInfoTextAreaInput')
    public internalInfoTextAreaInput: ElementRef;

    public textInputControl: FormControl;

    public modelType: TEXT_INPUT_MODEL;

    public faBarcode = faBarcode;
    private readonly emojisRegex: string;

    private isUserFocusEvent = true;
    private formatMultilineTextOnBlur = false;

    private destroySubject = new Subject<void>();

    private readonly barcodeScanner = inject<BarcodeScannerInterface>(BarcodeScannerInstanceToken);

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly formBuilder: FormBuilder,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.formatMultilineTextOnBlur = this.configService.getFormatMultilineTextOnBlur();
        this.emojisRegex = this.configService.getEmojisRegex();
    }

    public ngOnInit(): void {
        this.modelType =
            this.model instanceof MultilineTextModel
                ? TEXT_INPUT_MODEL.MULTILINE_TEXT_MODEL
                : this.model instanceof InternalInfoMultilineTextModel
                  ? TEXT_INPUT_MODEL.INTERNAL_INFO_MULTILINE_TEXT_MODEL
                  : TEXT_INPUT_MODEL.TEXT_MODEL;

        const textValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);
        this.textInputControl = this.formBuilder.control(
            { value: this.model.value, disabled: this.model.options.readonly },
            textValidator,
        );

        // if this is a multiline text input that should be formatted only when the user stops typing and leaves the
        // field, then all formatting and updating should happen only on blur; otherwise we use the regular approach
        // of listening to valueChanges
        iif(
            () => this.commitsOnBlurOnly(),
            this.blurEvent.asObservable(),
            this.textInputControl.valueChanges.pipe(debounceTime(ConfigConstants.debounceTimeMs)),
        )
            .pipe(
                // We can't use distinctUntilChanged() because the underlying object changed and distinctUntilChanged detects it as a change and bypasses this //
                filter((newVal: string) => this.model.value !== newVal),
                takeUntil(this.destroySubject),
            )
            .subscribe((newVal: string) => this.commitValue(newVal));

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.textInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in TextInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    ),
                );
        }

        // In case of MultilineText, where formatting is done only on blur,
        // if there is any pre-filled text which is not formatted, then when the screen loads, the input field needs to be focused and blurred to format the text.
        // So instead we are calling this function to format the text on init.
        this.formatMultilineText();
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.textInputControl) {
            if (this.model.options.readonly && this.textInputControl.enabled) {
                this.textInputControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.textInputControl.disabled) {
                this.textInputControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.model = <TextModel | MultilineTextModel | InternalInfoMultilineTextModel>changesObj.model.currentValue;
            this.textInputControl.setValue(this.model.value);
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.textInputControl);
            this.parentForm.controls[index].markAsDirty();
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public doScan(): void {
        this.barcodeScanner
            .scan(
                this.model.options.patternScan,
                this.model.options.applicationIdentifier,
                this.barcodeClipboardAccessible,
            )
            .pipe(
                take(1),
                tap((scanResult) => {
                    // check if result matches validation pattern
                    this.log.debug(`TextInputComponent: doScan: Result from pattern scan: ${scanResult.text}`);
                    const patternResult = matchAndProcessBarcode(
                        scanResult.text,
                        this.model.options.patternScan,
                        scanResult.format,
                    );
                    this.textInputControl.setValue(patternResult);
                    this.scanResults.emit(patternResult);
                }),
                catchError((error: unknown) => {
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('TEXT_SCAN_ERROR_NO_TITLE'),
                        <string>this.translate.instant('TEXT_SCAN_ERROR_NO_BODY', { error: JSON.stringify(error) }),
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const controlValue = control.value as string;

        if (this.model.options.required && !controlValue) {
            errors.required = { required: 'this input is mandatory' };
        }
        if (controlValue && controlValue.toString().length > this.model.options.maxLength) {
            errors.maxlength = { maxlength: 'maxlength exceeded' };
        }
        if (controlValue && controlValue.toString().length < this.model.options.minLength) {
            errors.minlength = { minlength: 'minlength not reached' };
        }
        const patternCheckResult = isDefined(controlValue) ? controlValue.match(this.model.options.pattern) : undefined;
        if (
            controlValue &&
            typeof patternCheckResult !== 'undefined' &&
            (!patternCheckResult || patternCheckResult[0] !== controlValue)
        ) {
            // a model may supply its own translation key to explain the expected format,
            // otherwise the generic pattern message is used
            errors.pattern = {
                pattern: 'pattern does not match',
                ...(this.model.options.patternErrorMessage ? { text: this.model.options.patternErrorMessage } : {}),
            };
        }

        // SHCNGMP58-1954
        if (
            (this.model instanceof MultilineTextModel || this.model instanceof InternalInfoMultilineTextModel) &&
            this.model.options.maxLines !== Infinity &&
            controlValue.split('\n').length > this.model.options.maxLines
        ) {
            errors.maxLines = { maxLines: 'maximum number of lines exceeded' };
        }

        const emojisCheckResult =
            this.emojisRegex.length > 0 && isDefined(controlValue)
                ? controlValue.match(new RegExp(this.emojisRegex, 'g'))
                : // allowing null since String.match could return null as well

                  null;
        if (emojisCheckResult) {
            // creating a set out of the RegExpMatchArray removes duplicate characters, spreading the set into an array
            // and joining it gives us a single string containing all the forbidden characters
            errors.forbiddenCharacters = { forbiddenCharacters: [...new Set(emojisCheckResult)].join('') };
        }
        // allowing null because angular requires it in the signature of a validator function

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public formatMultilineText(): void {
        if (this.model instanceof MultilineTextModel || this.model instanceof InternalInfoMultilineTextModel) {
            this.model.trimLines();
            if (this.model.options.formatMultilineTextPattern) {
                // get the formatted text based on the line length
                const newText = this.model.getFormattedText();
                // set the control value so it update the UI textarea
                this.textInputControl.setValue(newText);
            }
        }
    }

    public onBlur(): void {
        this.blurEvent.emit(this.textInputControl.value);
        // The debounced valueChanges path (see ngOnInit) already commits the value shortly after typing
        // stops, but leaving the field — by blurring, e.g. because a button was clicked — can happen before
        // that debounce elapses. Commit immediately here so a handler reacting to that same blur (or a click
        // right after it) never reads a stale/partial value. Skipped when the blur-only path above already
        // handles the commit itself.
        if (!this.commitsOnBlurOnly()) {
            this.commitControlValueIfChanged();
        }
    }

    /**
     * Pressing Enter is an explicit "submit now" signal — e.g. a sibling "Add" button in the same form
     * relying on the model being current. Commit immediately for the same reason as onBlur(); only wired up
     * on the single-line input (see the template), so this never fires for a multiline field.
     */
    public onEnter(): void {
        if (!this.commitsOnBlurOnly()) {
            this.commitControlValueIfChanged();
        }
    }

    private commitsOnBlurOnly(): boolean {
        return (
            (this.model instanceof MultilineTextModel || this.model instanceof InternalInfoMultilineTextModel) &&
            !!this.model.options.formatMultilineTextPattern &&
            this.formatMultilineTextOnBlur
        );
    }

    private commitControlValueIfChanged(): void {
        const newVal = <string>this.textInputControl.value;
        if (this.model.value !== newVal) {
            this.commitValue(newVal);
        }
    }

    private commitValue(newVal: string): void {
        const oldValue = this.model.value;
        this.model.value = newVal;
        if (
            (this.model instanceof MultilineTextModel || this.model instanceof InternalInfoMultilineTextModel) &&
            this.model.options.formatMultilineTextPattern
        ) {
            // certain text area fields like report text and trouble text have defined formatting
            // for example each line should contain only a fixed num of characters (65 or 90)
            // otherwise the word should be moved to next line
            // if the text gets changed after applying formatting then do not emit the modelChange and valueChange events yet
            // set the new value of textInputControl so that UI displays formatted text and then emit further events
            this.formatMultilineText();
            if (this.model instanceof MultilineTextModel) {
                return;
            }
        }
        this.modelChange.emit(this.model);
        this.valueChange.emit({
            oldValue: oldValue,
            newValue: this.model.value,
        });
    }

    public onFocus(): void {
        // set cursor position if one is set in the model options
        if (
            this.model.options instanceof TextModelOptions &&
            this.model.options.focusCursorPosition >= 0 &&
            this.isUserFocusEvent
        ) {
            const pos = this.model.options.focusCursorPosition;
            this.isUserFocusEvent = !this.isUserFocusEvent;
            setTimeout(() => {
                (<HTMLInputElement>this.textInput.nativeElement).setSelectionRange(pos, pos);
                (<HTMLInputElement>this.textInput.nativeElement).focus();
            }, 1);
        } else if (!this.isUserFocusEvent) {
            this.isUserFocusEvent = !this.isUserFocusEvent;
        }
    }
}

export interface IValueChanged {
    oldValue: string;
    newValue: string;
}
